import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  tournamentApi,
  AdminTournament,
  AdminTournamentInput,
  gamificationApi,
} from '@/api/gamification'
import Badge from '@/components/ui/Badge'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  TrophyIcon,
  UserGroupIcon,
  QuestionMarkCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  ClockIcon,
  FlagIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline'

const statusLabels: Record<string, string> = {
  draft: 'Bozza',
  registration: 'Iscrizioni Aperte',
  in_progress: 'In Corso',
  completed: 'Completato',
  archived: 'Archiviato',
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  registration: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-green-100 text-green-700',
  completed: 'bg-purple-100 text-purple-700',
  archived: 'bg-gray-100 text-gray-500',
}

const questionTypeLabels: Record<string, string> = {
  single: 'Risposta Singola',
  multiple: 'Risposta Multipla',
  true_false: 'Vero/Falso',
}

export default function TorneiAdminPage() {
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showQuestionsModal, setShowQuestionsModal] = useState(false)
  const [showParticipantsModal, setShowParticipantsModal] = useState(false)
  const [selectedTournament, setSelectedTournament] = useState<AdminTournament | null>(null)
  const [expandedTournament, setExpandedTournament] = useState<number | null>(null)

  // Form state for create/edit
  const [formData, setFormData] = useState<AdminTournamentInput>({
    title: '',
    description: '',
    rules: '',
    registration_start: '',
    registration_end: '',
    start_date: '',
    end_date: '',
    time_limit_minutes: 30,
    max_participants: undefined,
    max_attempts: 1,
    questions_count: 10,
    shuffle_questions: true,
    show_correct_answers: false,
    show_live_leaderboard: true,
    points_reward_1st: 100,
    points_reward_2nd: 50,
    points_reward_3rd: 25,
    points_participation: 10,
    badge_winner: undefined,
    badge_participant: undefined,
    status: 'draft',
    is_active: true,
    min_role: 'member',
  })

  // Question form state
  const [questionForm, setQuestionForm] = useState({
    text: '',
    type: 'single',
    points: 10,
    explanation: '',
    answers: [
      { text: '', is_correct: false },
      { text: '', is_correct: false },
    ],
  })
  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(null)
  const [deletingQuestionId, setDeletingQuestionId] = useState<number | null>(null)

  // Queries
  const { data: tournaments, isLoading } = useQuery({
    queryKey: ['admin-tournaments', filterStatus],
    queryFn: () => tournamentApi.getAdminTournaments(
      filterStatus !== 'all' ? { status: filterStatus } : undefined
    ),
  })

  const { data: badges } = useQuery({
    queryKey: ['admin-badges-for-tournaments'],
    queryFn: () => gamificationApi.getAdminBadges(),
  })

  const { data: questions, isLoading: loadingQuestions } = useQuery({
    queryKey: ['tournament-questions', selectedTournament?.slug],
    queryFn: () => tournamentApi.getQuestions(selectedTournament!.slug),
    enabled: !!selectedTournament && showQuestionsModal,
  })

  const { data: participants, isLoading: loadingParticipants } = useQuery({
    queryKey: ['tournament-participants', selectedTournament?.slug],
    queryFn: () => tournamentApi.getParticipants(selectedTournament!.slug),
    enabled: !!selectedTournament && showParticipantsModal,
  })

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: AdminTournamentInput) => tournamentApi.createTournament(data),
    onSuccess: () => {
      toast.success('Torneo creato con successo')
      queryClient.invalidateQueries({ queryKey: ['admin-tournaments'] })
      setShowCreateModal(false)
      resetForm()
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      toast.error(error.response?.data?.detail || 'Errore nella creazione del torneo')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ slug, data }: { slug: string; data: Partial<AdminTournamentInput> }) =>
      tournamentApi.updateTournament(slug, data),
    onSuccess: () => {
      toast.success('Torneo aggiornato con successo')
      queryClient.invalidateQueries({ queryKey: ['admin-tournaments'] })
      setShowEditModal(false)
      setSelectedTournament(null)
      resetForm()
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento del torneo')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (slug: string) => tournamentApi.deleteTournament(slug),
    onSuccess: () => {
      toast.success('Torneo eliminato')
      queryClient.invalidateQueries({ queryKey: ['admin-tournaments'] })
      setShowDeleteModal(false)
      setSelectedTournament(null)
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione del torneo')
    },
  })

  const addQuestionMutation = useMutation({
    mutationFn: ({ slug, data }: { slug: string; data: typeof questionForm }) =>
      tournamentApi.addQuestion(slug, data),
    onSuccess: () => {
      toast.success('Domanda aggiunta')
      queryClient.invalidateQueries({ queryKey: ['tournament-questions', selectedTournament?.slug] })
      resetQuestionForm()
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta della domanda')
    },
  })

  const updateQuestionMutation = useMutation({
    mutationFn: ({ slug, questionId, data }: { slug: string; questionId: number; data: typeof questionForm }) =>
      tournamentApi.updateQuestion(slug, questionId, data),
    onSuccess: () => {
      toast.success('Domanda aggiornata')
      queryClient.invalidateQueries({ queryKey: ['tournament-questions', selectedTournament?.slug] })
      setEditingQuestionId(null)
      resetQuestionForm()
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento della domanda')
    },
  })

  const deleteQuestionMutation = useMutation({
    mutationFn: ({ slug, questionId }: { slug: string; questionId: number }) =>
      tournamentApi.deleteQuestion(slug, questionId),
    onSuccess: () => {
      toast.success('Domanda eliminata')
      queryClient.invalidateQueries({ queryKey: ['tournament-questions', selectedTournament?.slug] })
      setDeletingQuestionId(null)
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione della domanda')
    },
  })

  const finalizeMutation = useMutation({
    mutationFn: (slug: string) => tournamentApi.finalizeTournament(slug),
    onSuccess: (data) => {
      toast.success(data.detail)
      queryClient.invalidateQueries({ queryKey: ['admin-tournaments'] })
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      toast.error(error.response?.data?.detail || 'Errore nella finalizzazione del torneo')
    },
  })

  // Helpers
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      rules: '',
      registration_start: '',
      registration_end: '',
      start_date: '',
      end_date: '',
      time_limit_minutes: 30,
      max_participants: undefined,
      max_attempts: 1,
      questions_count: 10,
      shuffle_questions: true,
      show_correct_answers: false,
      show_live_leaderboard: true,
      points_reward_1st: 100,
      points_reward_2nd: 50,
      points_reward_3rd: 25,
      points_participation: 10,
      badge_winner: undefined,
      badge_participant: undefined,
      status: 'draft',
      is_active: true,
      min_role: 'member',
    })
  }

  const resetQuestionForm = () => {
    setQuestionForm({
      text: '',
      type: 'single',
      points: 10,
      explanation: '',
      answers: [
        { text: '', is_correct: false },
        { text: '', is_correct: false },
      ],
    })
    setEditingQuestionId(null)
  }

  const loadQuestionForEdit = (question: { id: number; text: string; type: string; points: number; explanation?: string; answers: { text: string; is_correct: boolean }[] }) => {
    setEditingQuestionId(question.id)
    setQuestionForm({
      text: question.text,
      type: question.type,
      points: question.points,
      explanation: question.explanation || '',
      answers: question.answers.length > 0
        ? question.answers.map(a => ({ text: a.text, is_correct: a.is_correct }))
        : [{ text: '', is_correct: false }, { text: '', is_correct: false }],
    })
  }

  const handleUpdateQuestion = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedTournament || !editingQuestionId) return

    const hasCorrectAnswer = questionForm.answers.some(a => a.is_correct)
    if (!hasCorrectAnswer) {
      toast.error('Seleziona almeno una risposta corretta')
      return
    }

    const validAnswers = questionForm.answers.filter(a => a.text.trim())
    if (validAnswers.length < 2) {
      toast.error('Inserisci almeno 2 risposte')
      return
    }

    updateQuestionMutation.mutate({
      slug: selectedTournament.slug,
      questionId: editingQuestionId,
      data: { ...questionForm, answers: validAnswers },
    })
  }

  const handleDeleteQuestion = (questionId: number) => {
    if (!selectedTournament) return
    deleteQuestionMutation.mutate({
      slug: selectedTournament.slug,
      questionId,
    })
  }

  const loadTournamentForEdit = (tournament: AdminTournament) => {
    setSelectedTournament(tournament)
    setFormData({
      title: tournament.title,
      description: tournament.description,
      rules: tournament.rules || '',
      registration_start: tournament.registration_start.slice(0, 16),
      registration_end: tournament.registration_end.slice(0, 16),
      start_date: tournament.start_date.slice(0, 16),
      end_date: tournament.end_date.slice(0, 16),
      time_limit_minutes: tournament.time_limit_minutes,
      max_participants: tournament.max_participants,
      max_attempts: tournament.max_attempts,
      questions_count: tournament.questions_count,
      shuffle_questions: tournament.shuffle_questions,
      show_correct_answers: tournament.show_correct_answers,
      show_live_leaderboard: tournament.show_live_leaderboard,
      points_reward_1st: tournament.points_reward_1st,
      points_reward_2nd: tournament.points_reward_2nd,
      points_reward_3rd: tournament.points_reward_3rd,
      points_participation: tournament.points_participation,
      badge_winner: tournament.badge_winner?.id,
      badge_participant: tournament.badge_participant?.id,
      status: tournament.status as AdminTournamentInput['status'],
      is_active: tournament.is_active,
      min_role: tournament.min_role,
    })
    setShowEditModal(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (showEditModal && selectedTournament) {
      updateMutation.mutate({ slug: selectedTournament.slug, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleAddQuestion = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedTournament) return

    // Validate at least one correct answer
    const hasCorrectAnswer = questionForm.answers.some(a => a.is_correct)
    if (!hasCorrectAnswer) {
      toast.error('Seleziona almeno una risposta corretta')
      return
    }

    // Filter empty answers
    const validAnswers = questionForm.answers.filter(a => a.text.trim())
    if (validAnswers.length < 2) {
      toast.error('Inserisci almeno 2 risposte')
      return
    }

    addQuestionMutation.mutate({
      slug: selectedTournament.slug,
      data: { ...questionForm, answers: validAnswers },
    })
  }

  const addAnswer = () => {
    if (questionForm.answers.length < 6) {
      setQuestionForm(prev => ({
        ...prev,
        answers: [...prev.answers, { text: '', is_correct: false }],
      }))
    }
  }

  const removeAnswer = (index: number) => {
    if (questionForm.answers.length > 2) {
      setQuestionForm(prev => ({
        ...prev,
        answers: prev.answers.filter((_, i) => i !== index),
      }))
    }
  }

  const filteredTournaments = tournaments?.filter(t =>
    t.title.toLowerCase().includes(searchQuery.toLowerCase())
  ) || []

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Tornei</h1>
          <p className="text-gray-600">Crea e gestisci i tornei quiz della piattaforma</p>
        </div>
        <button
          onClick={() => {
            resetForm()
            setShowCreateModal(true)
          }}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <PlusIcon className="h-5 w-5" />
          Nuovo Torneo
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca tornei..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">Tutti gli stati</option>
            <option value="draft">Bozza</option>
            <option value="registration">Iscrizioni Aperte</option>
            <option value="in_progress">In Corso</option>
            <option value="completed">Completato</option>
            <option value="archived">Archiviato</option>
          </select>
        </div>
      </div>

      {/* Tournament List */}
      <div className="bg-white rounded-lg shadow-sm">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Caricamento tornei...</div>
        ) : filteredTournaments.length === 0 ? (
          <div className="p-8 text-center">
            <TrophyIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Nessun torneo trovato</p>
            <button
              onClick={() => {
                resetForm()
                setShowCreateModal(true)
              }}
              className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
            >
              Crea il primo torneo
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredTournaments.map((tournament) => (
              <div key={tournament.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-gray-900">{tournament.title}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[tournament.status]}`}>
                        {statusLabels[tournament.status]}
                      </span>
                      {!tournament.is_active && (
                        <Badge variant="warning">Inattivo</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{tournament.description}</p>

                    {/* Stats Row */}
                    <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <UserGroupIcon className="h-4 w-4" />
                        {tournament.participants_count} partecipanti
                      </span>
                      <span className="flex items-center gap-1">
                        <QuestionMarkCircleIcon className="h-4 w-4" />
                        {tournament.questions_created}/{tournament.questions_count} domande
                      </span>
                      <span className="flex items-center gap-1">
                        <ClockIcon className="h-4 w-4" />
                        {tournament.time_limit_minutes} min
                      </span>
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="h-4 w-4" />
                        {formatDate(tournament.start_date)}
                      </span>
                    </div>

                    {/* Expanded Details */}
                    {expandedTournament === tournament.id && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Iscrizioni</p>
                          <p className="font-medium">{formatDate(tournament.registration_start)}</p>
                          <p className="text-gray-400">→ {formatDate(tournament.registration_end)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Svolgimento</p>
                          <p className="font-medium">{formatDate(tournament.start_date)}</p>
                          <p className="text-gray-400">→ {formatDate(tournament.end_date)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Premi Punti</p>
                          <p className="font-medium">🥇 {tournament.points_reward_1st} pts</p>
                          <p className="text-gray-400">🥈 {tournament.points_reward_2nd} • 🥉 {tournament.points_reward_3rd}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Opzioni</p>
                          <p className="font-medium">
                            {tournament.max_attempts} tentativ{tournament.max_attempts > 1 ? 'i' : 'o'}
                          </p>
                          <p className="text-gray-400">
                            {tournament.shuffle_questions ? 'Domande mescolate' : 'Ordine fisso'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => setExpandedTournament(expandedTournament === tournament.id ? null : tournament.id)}
                      className="p-2 text-gray-400 hover:text-gray-600"
                      title="Dettagli"
                    >
                      {expandedTournament === tournament.id ? (
                        <ChevronUpIcon className="h-5 w-5" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTournament(tournament)
                        setShowQuestionsModal(true)
                      }}
                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                      title="Gestisci Domande"
                    >
                      <QuestionMarkCircleIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTournament(tournament)
                        setShowParticipantsModal(true)
                      }}
                      className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
                      title="Vedi Partecipanti"
                    >
                      <UserGroupIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => loadTournamentForEdit(tournament)}
                      className="p-2 text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded"
                      title="Modifica"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    {tournament.status === 'completed' && (
                      <button
                        onClick={() => finalizeMutation.mutate(tournament.slug)}
                        className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded"
                        title="Finalizza e assegna premi"
                        disabled={finalizeMutation.isPending}
                      >
                        <FlagIcon className="h-5 w-5" />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setSelectedTournament(tournament)
                        setShowDeleteModal(true)
                      }}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                      title="Elimina"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4">
              <h2 className="text-lg font-semibold">
                {showEditModal ? 'Modifica Torneo' : 'Nuovo Torneo'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Es: Quiz Compliance Q1 2025"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione *</label>
                  <textarea
                    required
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Descrivi il torneo..."
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Regolamento</label>
                  <textarea
                    rows={2}
                    value={formData.rules}
                    onChange={(e) => setFormData(prev => ({ ...prev, rules: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Regole del torneo (opzionale)..."
                  />
                </div>
              </div>

              {/* Dates */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Date e Orari</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Inizio Iscrizioni *</label>
                    <input
                      type="datetime-local"
                      required
                      value={formData.registration_start}
                      onChange={(e) => setFormData(prev => ({ ...prev, registration_start: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fine Iscrizioni *</label>
                    <input
                      type="datetime-local"
                      required
                      value={formData.registration_end}
                      onChange={(e) => setFormData(prev => ({ ...prev, registration_end: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Inizio Torneo *</label>
                    <input
                      type="datetime-local"
                      required
                      value={formData.start_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fine Torneo *</label>
                    <input
                      type="datetime-local"
                      required
                      value={formData.end_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
              </div>

              {/* Quiz Settings */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Impostazioni Quiz</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tempo (min) *</label>
                    <input
                      type="number"
                      required
                      min={1}
                      value={formData.time_limit_minutes}
                      onChange={(e) => setFormData(prev => ({ ...prev, time_limit_minutes: Number(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">N. Domande *</label>
                    <input
                      type="number"
                      required
                      min={1}
                      value={formData.questions_count}
                      onChange={(e) => setFormData(prev => ({ ...prev, questions_count: Number(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Tentativi</label>
                    <input
                      type="number"
                      min={1}
                      value={formData.max_attempts}
                      onChange={(e) => setFormData(prev => ({ ...prev, max_attempts: Number(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Partecipanti</label>
                    <input
                      type="number"
                      min={0}
                      placeholder="Illimitati"
                      value={formData.max_participants || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        max_participants: e.target.value ? Number(e.target.value) : undefined
                      }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-6 mt-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.shuffle_questions}
                      onChange={(e) => setFormData(prev => ({ ...prev, shuffle_questions: e.target.checked }))}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Mescola domande</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.show_correct_answers}
                      onChange={(e) => setFormData(prev => ({ ...prev, show_correct_answers: e.target.checked }))}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Mostra risposte corrette</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.show_live_leaderboard}
                      onChange={(e) => setFormData(prev => ({ ...prev, show_live_leaderboard: e.target.checked }))}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Classifica live</span>
                  </label>
                </div>
              </div>

              {/* Points & Rewards */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Punti e Premi</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">🥇 1° posto</label>
                    <input
                      type="number"
                      min={0}
                      value={formData.points_reward_1st}
                      onChange={(e) => setFormData(prev => ({ ...prev, points_reward_1st: Number(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">🥈 2° posto</label>
                    <input
                      type="number"
                      min={0}
                      value={formData.points_reward_2nd}
                      onChange={(e) => setFormData(prev => ({ ...prev, points_reward_2nd: Number(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">🥉 3° posto</label>
                    <input
                      type="number"
                      min={0}
                      value={formData.points_reward_3rd}
                      onChange={(e) => setFormData(prev => ({ ...prev, points_reward_3rd: Number(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Partecipazione</label>
                    <input
                      type="number"
                      min={0}
                      value={formData.points_participation}
                      onChange={(e) => setFormData(prev => ({ ...prev, points_participation: Number(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Badge Vincitore</label>
                    <select
                      value={formData.badge_winner || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        badge_winner: e.target.value ? Number(e.target.value) : undefined
                      }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      <option value="">Nessun badge</option>
                      {badges?.map(badge => (
                        <option key={badge.id} value={badge.id}>🏆 {badge.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Badge Partecipante</label>
                    <select
                      value={formData.badge_participant || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        badge_participant: e.target.value ? Number(e.target.value) : undefined
                      }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      <option value="">Nessun badge</option>
                      {badges?.map(badge => (
                        <option key={badge.id} value={badge.id}>🎖️ {badge.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Status & Access */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Stato e Accesso</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stato</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        status: e.target.value as AdminTournamentInput['status']
                      }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      <option value="draft">Bozza</option>
                      <option value="registration">Iscrizioni Aperte</option>
                      <option value="in_progress">In Corso</option>
                      <option value="completed">Completato</option>
                      <option value="archived">Archiviato</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo Minimo</label>
                    <select
                      value={formData.min_role}
                      onChange={(e) => setFormData(prev => ({ ...prev, min_role: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      <option value="member">Member</option>
                      <option value="premium">Premium</option>
                      <option value="staff">Staff</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 pb-2">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Torneo attivo</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    setShowEditModal(false)
                    setSelectedTournament(null)
                    resetForm()
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Salvataggio...'
                    : showEditModal
                    ? 'Aggiorna Torneo'
                    : 'Crea Torneo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedTournament && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Elimina Torneo</h3>
            <p className="text-gray-600 mb-6">
              Sei sicuro di voler eliminare il torneo <strong>{selectedTournament.title}</strong>?
              {selectedTournament.participants_count > 0 && (
                <span className="block mt-2 text-red-600">
                  Attenzione: ci sono {selectedTournament.participants_count} partecipanti iscritti.
                </span>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setSelectedTournament(null)
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={() => deleteMutation.mutate(selectedTournament.slug)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Eliminazione...' : 'Elimina'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Questions Modal */}
      {showQuestionsModal && selectedTournament && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Domande: {selectedTournament.title}</h2>
                <p className="text-sm text-gray-500">
                  {questions?.length || 0}/{selectedTournament.questions_count} domande create
                </p>
              </div>
              <button
                onClick={() => {
                  setShowQuestionsModal(false)
                  setSelectedTournament(null)
                  resetQuestionForm()
                }}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <XCircleIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Add/Edit Question Form */}
              <form onSubmit={editingQuestionId ? handleUpdateQuestion : handleAddQuestion} className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium">
                    {editingQuestionId ? 'Modifica Domanda' : 'Aggiungi Nuova Domanda'}
                  </h3>
                  {editingQuestionId && (
                    <button
                      type="button"
                      onClick={resetQuestionForm}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Annulla modifica
                    </button>
                  )}
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Testo Domanda *</label>
                    <textarea
                      required
                      rows={2}
                      value={questionForm.text}
                      onChange={(e) => setQuestionForm(prev => ({ ...prev, text: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="Inserisci la domanda..."
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                      <select
                        value={questionForm.type}
                        onChange={(e) => setQuestionForm(prev => ({ ...prev, type: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      >
                        <option value="single">Risposta Singola</option>
                        <option value="multiple">Risposta Multipla</option>
                        <option value="true_false">Vero/Falso</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Punti</label>
                      <input
                        type="number"
                        min={1}
                        value={questionForm.points}
                        onChange={(e) => setQuestionForm(prev => ({ ...prev, points: Number(e.target.value) }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Spiegazione</label>
                      <input
                        type="text"
                        value={questionForm.explanation}
                        onChange={(e) => setQuestionForm(prev => ({ ...prev, explanation: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        placeholder="Opzionale"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">Risposte *</label>
                      <button
                        type="button"
                        onClick={addAnswer}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        + Aggiungi risposta
                      </button>
                    </div>
                    <div className="space-y-2">
                      {questionForm.answers.map((answer, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type={questionForm.type === 'multiple' ? 'checkbox' : 'radio'}
                            name="correct_answer"
                            checked={answer.is_correct}
                            onChange={() => {
                              if (questionForm.type === 'multiple') {
                                setQuestionForm(prev => ({
                                  ...prev,
                                  answers: prev.answers.map((a, i) =>
                                    i === index ? { ...a, is_correct: !a.is_correct } : a
                                  ),
                                }))
                              } else {
                                setQuestionForm(prev => ({
                                  ...prev,
                                  answers: prev.answers.map((a, i) => ({
                                    ...a,
                                    is_correct: i === index,
                                  })),
                                }))
                              }
                            }}
                            className="h-4 w-4 text-primary-600"
                          />
                          <input
                            type="text"
                            value={answer.text}
                            onChange={(e) => {
                              setQuestionForm(prev => ({
                                ...prev,
                                answers: prev.answers.map((a, i) =>
                                  i === index ? { ...a, text: e.target.value } : a
                                ),
                              }))
                            }}
                            placeholder={`Risposta ${index + 1}`}
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                          />
                          {questionForm.answers.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeAnswer(index)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={addQuestionMutation.isPending || updateQuestionMutation.isPending}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      {editingQuestionId
                        ? (updateQuestionMutation.isPending ? 'Salvataggio...' : 'Salva Modifiche')
                        : (addQuestionMutation.isPending ? 'Aggiunta...' : 'Aggiungi Domanda')
                      }
                    </button>
                  </div>
                </div>
              </form>

              {/* Questions List */}
              <div>
                <h3 className="font-medium mb-4">Domande Esistenti ({questions?.length || 0})</h3>
                {loadingQuestions ? (
                  <div className="text-center py-8 text-gray-500">Caricamento...</div>
                ) : questions && questions.length > 0 ? (
                  <div className="space-y-3">
                    {questions.map((q, index) => (
                      <div key={q.id} className={`border rounded-lg p-4 ${editingQuestionId === q.id ? 'ring-2 ring-primary-500 bg-primary-50' : ''}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                              <Badge variant="default">{questionTypeLabels[q.type] || q.type}</Badge>
                              <Badge variant="primary">{q.points} pts</Badge>
                            </div>
                            <p className="text-gray-900 font-medium">{q.text}</p>
                            <div className="mt-2 grid grid-cols-2 gap-2">
                              {q.answers.map((a) => (
                                <div
                                  key={a.id}
                                  className={`text-sm px-2 py-1 rounded ${
                                    a.is_correct
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {a.is_correct && <CheckCircleIcon className="inline h-4 w-4 mr-1" />}
                                  {a.text}
                                </div>
                              ))}
                            </div>
                            {q.explanation && (
                              <p className="text-sm text-gray-500 mt-2 italic">
                                💡 {q.explanation}
                              </p>
                            )}
                          </div>
                          {/* Edit/Delete Buttons */}
                          <div className="flex flex-col gap-2">
                            <button
                              type="button"
                              onClick={() => loadQuestionForEdit(q)}
                              className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
                              title="Modifica domanda"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            {deletingQuestionId === q.id ? (
                              <div className="flex flex-col gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleDeleteQuestion(q.id)}
                                  disabled={deleteQuestionMutation.isPending}
                                  className="p-1 text-xs text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50"
                                >
                                  {deleteQuestionMutation.isPending ? '...' : 'Conferma'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeletingQuestionId(null)}
                                  className="p-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                                >
                                  Annulla
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setDeletingQuestionId(q.id)}
                                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                title="Elimina domanda"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Nessuna domanda ancora. Aggiungi la prima domanda sopra.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Participants Modal */}
      {showParticipantsModal && selectedTournament && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Partecipanti: {selectedTournament.title}</h2>
                <p className="text-sm text-gray-500">
                  {participants?.length || 0} partecipanti
                </p>
              </div>
              <button
                onClick={() => {
                  setShowParticipantsModal(false)
                  setSelectedTournament(null)
                }}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <XCircleIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loadingParticipants ? (
                <div className="p-8 text-center text-gray-500">Caricamento...</div>
              ) : participants && participants.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pos.</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partecipante</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Punteggio</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tempo</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tentativi</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {participants
                      .sort((a, b) => (a.final_rank || 999) - (b.final_rank || 999))
                      .map((p) => (
                        <tr key={p.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            {p.final_rank ? (
                              <span className={`font-bold ${
                                p.final_rank === 1 ? 'text-yellow-600' :
                                p.final_rank === 2 ? 'text-gray-500' :
                                p.final_rank === 3 ? 'text-orange-600' : 'text-gray-400'
                              }`}>
                                {p.final_rank === 1 ? '🥇' : p.final_rank === 2 ? '🥈' : p.final_rank === 3 ? '🥉' : `#${p.final_rank}`}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              {(p.user.avatar_url || p.user.avatar) ? (
                                <img src={p.user.avatar_url || p.user.avatar} alt="" className="h-8 w-8 rounded-full" />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-medium">
                                  {(p.user.display_name || p.user.full_name || '?').charAt(0)}
                                </div>
                              )}
                              <span className="font-medium text-gray-900">{p.user.display_name || p.user.full_name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant={
                              p.status === 'completed' ? 'success' :
                              p.status === 'started' ? 'warning' :
                              p.status === 'disqualified' ? 'danger' : 'default'
                            }>
                              {p.status === 'registered' ? 'Iscritto' :
                               p.status === 'started' ? 'In corso' :
                               p.status === 'completed' ? 'Completato' : 'Squalificato'}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap font-medium">
                            {p.best_score !== undefined ? p.best_score : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                            {p.best_time_seconds
                              ? `${Math.floor(p.best_time_seconds / 60)}:${String(p.best_time_seconds % 60).padStart(2, '0')}`
                              : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                            {p.attempts_count}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  Nessun partecipante iscritto.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
